export interface EndUserMetadata {
  name?: string;
  email?: string;
  company?: string;
  // Any other trait a host page wants to track (plan_type, user_role,
  // a separate company_name field, etc.) -- Luisa: the dashboard's
  // analytics segment filter shows whatever real keys a tenant actually
  // sends here, nothing we define or hardcode on our side. Surfaced
  // verbatim on analytics events (see analytics.ts's identityMetadata());
  // never read by the identify()/conversation pipeline itself the way
  // name/email/company are.
  [key: string]: string | undefined;
}

/**
 * Either the HMAC pair (endUserId + userHash, signed by the customer's
 * backend) or a JWT the customer's frontend already has (verified by
 * ai-service against the tenant's configured JWKS/secret) — exactly one of
 * the two proves identity, metadata is always optional/unsigned on top.
 */
export type Identity =
  | {
      endUserId: string;
      userHash: string;
      idToken?: undefined;
      metadata?: EndUserMetadata;
      resolvedVisitorId?: string;
    }
  | {
      idToken: string;
      endUserId?: undefined;
      userHash?: undefined;
      metadata?: EndUserMetadata;
      // The real end_user_id, once the server resolves it (conversation
      // creation or identify()) -- a JWT (idToken-only) identity's own
      // end_user_id is decoded server-side only, never client-side, so
      // this is the only way analytics events on this identity ever get
      // tagged with anything other than the anonymous id. See
      // analytics.ts's resolveVisitorId.
      resolvedVisitorId?: string;
    };

const ANON_VISITOR_ID_KEY = "artis_anon_visitor_id";

/** Only for agents whose dashboard config explicitly opts into
 * public_identity (see WidgetConfig) — a public/anonymous site has no
 * logged-in user to identify, so this stands in for a real identify()
 * call: a stable id per browser, persisted in localStorage, never verified
 * server-side (ai-service trusts it as-is only because this specific agent
 * opted in — see _resolve_identity's public_identity branch). */
export function getOrCreateAnonymousVisitorId(): string {
  try {
    const existing = localStorage.getItem(ANON_VISITOR_ID_KEY);
    if (existing) return existing;
    const fresh = `anon-${crypto.randomUUID()}`;
    localStorage.setItem(ANON_VISITOR_ID_KEY, fresh);
    return fresh;
  } catch {
    // Private browsing / blocked storage — fall back to a per-page-load id
    // rather than failing to start a conversation at all.
    return `anon-${crypto.randomUUID()}`;
  }
}

export class IdentityStore {
  private identity: Identity | null = null;
  private listeners: Array<(identity: Identity) => void> = [];

  constructor(initial?: {
    endUserId?: string;
    userHash?: string;
    metadata?: EndUserMetadata;
  }) {
    if (initial?.endUserId && initial?.userHash) {
      this.identity = {
        endUserId: initial.endUserId,
        userHash: initial.userHash,
        metadata: initial.metadata,
      };
    }
  }

  get(): Identity | null {
    return this.identity;
  }

  set(endUserId: string, userHash: string, metadata?: EndUserMetadata): void {
    this.identity = { endUserId, userHash, metadata };
    for (const listener of this.listeners) listener(this.identity);
  }

  setToken(idToken: string, metadata?: EndUserMetadata): void {
    this.identity = { idToken, metadata };
    for (const listener of this.listeners) listener(this.identity);
  }

  /** Merges fields resolved server-side from a verified JWT (claim_path,
   * configured in Install -- see AgentIdentityField) into this same
   * metadata bag identify()/identifyWithToken() already fill, after
   * POST /widget/conversations resolves them. Unlike set()/setToken(),
   * never fires listeners: nothing needs to react to a metadata-only
   * change, only to endUserId actually changing (see analytics.ts's
   * onChange use). Every analytics event sent after this point carries
   * these fields, same as any other identify() trait -- events sent
   * before a call ever starts never do, since claim_path resolution is
   * tied to conversation creation today, not a standalone identify step. */
  mergeMetadata(extra: EndUserMetadata): void {
    if (!this.identity) return;
    this.identity = { ...this.identity, metadata: { ...this.identity.metadata, ...extra } };
  }

  /** Adopts the real end_user_id the server resolved (conversation
   * creation or identify()) -- unlike mergeMetadata, this FIRES
   * listeners: analytics.ts's own onChange handler is what actually
   * re-tags subsequent events and triggers the anon->real merge call,
   * the same mechanism the HMAC path already relies on, now also
   * reachable from the JWT path. No-ops if the id hasn't changed, so
   * calling it again with the same value (e.g. both endpoints resolving
   * the same id) doesn't cause a redundant merge call. */
  setResolvedVisitorId(id: string): void {
    if (!this.identity || this.identity.resolvedVisitorId === id) return;
    this.identity = { ...this.identity, resolvedVisitorId: id };
    for (const listener of this.listeners) listener(this.identity);
  }

  /** Clears identity and the persisted anonymous visitor id entirely --
   * call on logout. Without this, a second real person on a shared
   * device/browser would keep inheriting the first person's anon-id
   * history (see analytics.ts's merge-on-change logic): a fresh anon id
   * here means their next identify() starts clean instead. */
  reset(): void {
    this.identity = null;
    try {
      localStorage.removeItem(ANON_VISITOR_ID_KEY);
    } catch {
      // Private browsing / blocked storage -- nothing was persisted
      // anyway, same as getOrCreateAnonymousVisitorId's own fallback.
    }
  }

  onReady(listener: (identity: Identity) => void): void {
    if (this.identity) {
      listener(this.identity);
      return;
    }
    this.listeners.push(listener);
  }

  /** Unlike onReady (fires once -- now if an identity already exists, or
   * on the first future set()/setToken() otherwise -- and never again
   * after that), this always subscribes to every future set()/setToken()
   * call, without firing for whatever identity may already be set. For a
   * caller that needs to react to identity actually CHANGING (e.g.
   * analytics.ts merging an anonymous visitor into a real one once they
   * log in), not just "is there one yet" -- a public_identity agent
   * already has one (the anon id) before this is ever called, so onReady
   * would fire immediately with it and then never fire again. */
  onChange(listener: (identity: Identity) => void): void {
    this.listeners.push(listener);
  }
}
